import { useEffect, useState } from 'react'
import { ActionIcon, Alert, Button, Group, Modal, ScrollArea, Table, Tooltip } from '@mantine/core'
import { IconCheck, IconExternalLink, IconRefresh, IconUserCheck } from '@tabler/icons-react'
import { MODAL_CONTENT } from '@/components/ui/formModal.tokens'
import { CreateUpdatePatientModal } from '@/components/ui/CreateUpdatePatientModal'
import { cn } from '@/utils/cn'
import { canCreateChartFrom, prefillFromZocdoc } from './patientPrefill'
import { useZocdocPatientLink } from './useZocdocPatientLink'
import type { OrpcClient } from '@/hooks/useClient'
import { HINT, ID_CHIP, SECONDARY_BTN, TABLE_HEAD, TABLE_ROW } from './tokens'
import { formatDob, formatZocdocStart } from './zocdocFormat'

type AppointmentsResult = Awaited<ReturnType<OrpcClient['zocdocFetchAppointments']>>
export type ZocdocAppointmentRow = NonNullable<AppointmentsResult['appointments']>[number]

/** `2026-09-01T09:00:00-04:00` → `-04:00`. The offset Zocdoc sent, which is not the same as a zone. */
const offsetOf = (startAt?: string) => startAt?.slice(19) || null

/**
 * `2026-09-01T09:00:00-04:00` → `Tue, Sep 1 2026 · 9:00 AM`, read **at the offset Zocdoc sent**.
 *
 * Deliberately not localised to the viewer: an operator checking a booking needs the time the
 * patient and provider agreed on, not the same instant re-expressed in the operator's own zone.
 */
const readableStart = (startAt?: string) => {
  if (!startAt) return null
  const parsed = new Date(startAt)
  if (Number.isNaN(parsed.getTime())) return startAt
  const offset = offsetOf(startAt)
  // Rendering in the appointment's own offset means formatting in a fixed zone, so the parts are
  // shifted by hand rather than handed to a locale that would apply the browser's zone.
  const minutes =
    offset && /^[+-]\d{2}:\d{2}$/.test(offset)
      ? (offset.startsWith('-') ? -1 : 1) *
        (Number(offset.slice(1, 3)) * 60 + Number(offset.slice(4, 6)))
      : 0
  const shifted = new Date(parsed.getTime() + minutes * 60_000)
  return shifted.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  })
}

/** The viewer's own IANA zone, which is the zone the beHuman calendar renders in. */
const browserZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

/**
 * The same instant as beHuman stores it: a UTC `Z` string.
 *
 * This is the value the import matches on — `Slot?start=<this>` — so it is the one field on this
 * screen that has to be exact rather than readable.
 */
const utcInstant = (startAt?: string) => {
  if (!startAt) return null
  const parsed = new Date(startAt)
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString()
}

/** The same instant read in `timeZone`, e.g. `Tue, Sep 1 2026 · 4:00 PM`. */
const inZone = (startAt: string | undefined, timeZone: string) => {
  if (!startAt) return null
  const parsed = new Date(startAt)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  })
}

/**
 * What the mapped practitioner's beHuman calendar holds at this instant, as a sentence rather than
 * a chip — the list already carries the chip, and what an operator opening this modal needs is the
 * next move, not a second copy of the label.
 *
 * Keyed on the same `calendar.state` the row computes server-side, so this cannot drift from what
 * the import will actually find.
 */
const CALENDAR_VERDICT: Record<string, { tone: string; says: string }> = {
  synced: {
    tone: 'text-[#15803D]',
    says: 'Already imported — a beHuman appointment carries this Zocdoc id. Nothing to do.',
  },
  booked: {
    tone: 'text-[#1D4ED8]',
    says: 'A beHuman appointment already starts at this exact instant.',
  },
  'slot-free': {
    tone: 'text-[#1D4ED8]',
    says: 'A free slot exists at this instant — this appointment can be imported straight into it.',
  },
  'slot-claimed': {
    tone: 'text-[#B45309]',
    says: 'A slot exists at this instant but is already taken, so the import will refuse it.',
  },
  'no-slot': {
    tone: 'text-[#B45309]',
    says: 'No slot at this instant. Publish availability at the local time below, then import.',
  },
}

/**
 * A titled block with a rule above it, so the six sections read as separate answers rather than one
 * long list. The first block suppresses its rule — a line under the modal header would only double
 * the one Mantine already draws.
 */
function Section({
  title,
  first,
  actions,
  children,
}: {
  title: string
  first?: boolean
  /** Controls belonging to this section, right-aligned on the heading row. */
  actions?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className={cn([!first && 'border-t border-[#ECEEF1] pt-4'])}>
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
        <h3 className="behuman-v2-mulish m-0 !text-[11px] !font-bold !tracking-[0.06em] !text-[#6B7280] uppercase">
          {title}
        </h3>
        {actions}
      </div>
      {children}
    </section>
  )
}

function Facts({
  title,
  rows,
  first,
}: {
  title: string
  rows: [string, React.ReactNode][]
  first?: boolean
}) {
  const present = rows.filter(([, value]) => value !== null && value !== undefined && value !== '')
  if (present.length === 0) return null
  return (
    <Section title={title} first={first}>
      <dl className="m-0">
        {present.map(([label, value]) => (
          <div
            key={label}
            className="flex flex-wrap gap-x-4 gap-y-0.5 border-b border-[#ECEEF1] py-2 last:border-b-0"
          >
            <dt className="w-[150px] shrink-0 text-[13px] font-bold text-[#6B7280]">{label}</dt>
            <dd className="m-0 min-w-0 text-[13px] break-words text-[#1F2733]">{value}</dd>
          </div>
        ))}
      </dl>
    </Section>
  )
}

/**
 * The same instant on both systems' clocks, plus what beHuman's calendar holds there.
 *
 * The problem this solves: Zocdoc sends a wall clock in the **provider's** offset, beHuman stores
 * a UTC `Z`, and the beHuman calendar draws in the **operator's browser** zone. Three
 * representations of one moment, and an operator who has to publish availability before importing
 * needs the third one — the cell to click on the calendar — which no other screen shows.
 *
 * Everything here is derived from `startAt` and the row's own `calendar` state; nothing is fetched.
 */
function ScheduleComparison({ appointment }: { appointment: ZocdocAppointmentRow }) {
  const startAt = appointment.startAt
  const stored = utcInstant(startAt)
  const viewerZone = browserZone()
  const providerZone = appointment.timeZone
  const calendar = appointment.calendar
  const practitionerId = appointment.provider?.practitionerId

  if (!startAt || !stored) {
    return (
      <Section title="Same instant, both calendars">
        <p className={cn([HINT, 'm-0'])}>
          Zocdoc sent no usable start time for this appointment, so there is nothing to line up
          against the beHuman calendar.
        </p>
      </Section>
    )
  }

  // The provider's own wall clock comes from `readableStart`, which reads the offset Zocdoc sent
  // rather than a zone — correct even when the roster has no zone for this provider.
  const clocks: [string, React.ReactNode, string][] = [
    [
      providerZone ? `Zocdoc · ${providerZone}` : 'Zocdoc · provider',
      <span className="font-bold text-[#1F2733]">{readableStart(startAt)}</span>,
      'What the patient and provider agreed, and what Zocdoc’s own dashboard shows.',
    ],
    [
      'beHuman · stored',
      <span className={ID_CHIP}>{stored}</span>,
      'UTC, as every Slot, Appointment and Encounter is stored. The import matches on exactly this instant.',
    ],
    [
      `You · ${viewerZone}`,
      <span className="font-bold text-[#1F2733]">{inZone(startAt, viewerZone)}</span>,
      'Where this lands on the beHuman calendar, which draws in your browser’s zone.',
    ],
  ]

  const verdict = calendar ? CALENDAR_VERDICT[calendar.state] : undefined

  return (
    <Section title="Same instant, both calendars">
      <Table verticalSpacing={6} fz="xs">
        <Table.Thead>
          <Table.Tr className={TABLE_ROW}>
            <Table.Th className={cn([TABLE_HEAD, 'w-[190px]'])}>Clock</Table.Th>
            <Table.Th className={cn([TABLE_HEAD, 'w-[230px]'])}>Reads</Table.Th>
            <Table.Th className={TABLE_HEAD}>What it is for</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {clocks.map(([label, value, why]) => (
            <Table.Tr key={label} className={TABLE_ROW}>
              <Table.Td className="text-[12.5px] font-bold text-[#6B7280]">{label}</Table.Td>
              <Table.Td className="text-[13px]">{value}</Table.Td>
              <Table.Td className={HINT}>{why}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <div className="mt-3 rounded-lg bg-[#F8F9FB] p-3">
        {/* Three answers, and the distinction is the point: an unmapped provider has no calendar
            to compare against, a failed lookup is not an answer, and only the third is a verdict. */}
        {!practitionerId ? (
          <p className={cn([HINT, 'm-0'])}>
            This Zocdoc provider is not linked to a beHuman practitioner yet, so there is no
            calendar to compare against. Link the provider first — until then the times above are
            the whole answer.
          </p>
        ) : !calendar ? (
          <p className={cn([HINT, 'm-0'])}>
            The beHuman calendar could not be checked for this appointment, so whether a slot exists
            at this instant is unknown — not answered “no”.
          </p>
        ) : (
          <>
            <p className={cn(['m-0 text-[13px] font-bold', verdict?.tone ?? 'text-[#1F2733]'])}>
              {verdict?.says ?? `beHuman calendar: ${calendar.state}`}
            </p>
            {(calendar.state === 'no-slot' || calendar.state === 'slot-claimed') && (
              <p className={cn([HINT, 'mt-1.5 mb-0'])}>
                The slot has to start at{' '}
                <span className="font-bold text-[#1F2733]">{inZone(startAt, viewerZone)}</span> on
                your calendar — to the minute, because the import looks up{' '}
                <span className="font-mono whitespace-nowrap">Slot?start={stored}</span> exactly and
                a slot half a minute out will not match.{' '}
                <a
                  href={`/schedule?practitioner=${practitionerId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="behuman-v2-mulish font-bold text-[#E5533D] no-underline hover:underline"
                >
                  Open this practitioner’s calendar
                </a>{' '}
                — it opens on the current week, so navigate to the date above.
              </p>
            )}
          </>
        )}
      </div>
    </Section>
  )
}

/**
 * One Zocdoc appointment in full: when it is, in whose zone, and who is on it.
 *
 * Everything here rides on the list response — the participants read happens with the page load
 * (`includeParticipants`), so opening this makes no request and shows no spinner.
 *
 * **PHI.** Rendered for an admin on an unlisted page, and never logged; the middleware client logs
 * only its redacted summary.
 */
export function AppointmentDetailsModal({
  appointment,
  onClose,
  onImported,
}: {
  appointment: ZocdocAppointmentRow | null
  onClose: () => void
  /**
   * A chart was created and invited. The list's `associatedPatients` is stale the moment this
   * fires, so the caller reloads rather than leaving "no chart matched" on screen next to a chart
   * that now exists.
   */
  onImported?: () => void
}) {
  const [importing, setImporting] = useState(false)
  /** The chart the create form just made: its id, and whether it came back linked. */
  const [imported, setImported] = useState<{ id: string; linkFailure?: string } | null>(null)
  const participants = appointment?.participants
  const patient = participants?.patient
  const zocdocPatientId = patient?.patientId
  const { linking, linkError, setLinkError, confirmSamePatient, linkCreatedChart } =
    useZocdocPatientLink(zocdocPatientId, onImported)

  // The confirmation belongs to one appointment, so it clears when the modal moves to another.
  useEffect(() => {
    setImporting(false)
    setImported(null)
    setLinkError(null)
  }, [appointment?.id, setLinkError])

  const offset = offsetOf(appointment?.startAt)
  const address = [
    patient?.address1,
    patient?.address2,
    [patient?.city, patient?.state].filter(Boolean).join(', '),
    patient?.zipCode,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <>
      <Modal
        opened={appointment !== null}
        onClose={onClose}
        // Wider than the booking dialog: this one carries two-column fact lists and a candidate
        // table with five columns, which cramp badly at `lg`.
        size="60rem"
        radius={18}
        classNames={{
          content: MODAL_CONTENT,
          title: '!text-[17px] !font-extrabold !tracking-[-0.3px]',
        }}
        title="Appointment details"
      >
        {appointment && (
          <div className="flex flex-col gap-4">
            <Facts
              first
              title="When"
              rows={[
                ['Start', formatZocdocStart(appointment.startAt) ?? '—'],
                // The zone comes from the roster; the offset comes from the appointment itself. Both
                // are shown because either alone is incomplete: an offset cannot name a zone, and a
                // zone cannot tell you which side of a DST change this instant fell on.
                [
                  'Time zone',
                  appointment.timeZone ?? (
                    <span className={HINT}>
                      not in the provider roster — only the offset below is known
                    </span>
                  ),
                ],
                ['UTC offset', offset ? <span className={ID_CHIP}>{offset}</span> : null],
                [
                  'As Zocdoc sent it',
                  <span className={ID_CHIP}>{appointment.startAt ?? '—'}</span>,
                ],
                [
                  'End',
                  appointment.endAt ?? <span className={HINT}>Zocdoc returns no end time</span>,
                ],
              ]}
            />

            <ScheduleComparison appointment={appointment} />

            <Facts
              title="Appointment"
              rows={[
                ['Zocdoc id', <span className={ID_CHIP}>{appointment.id || '—'}</span>],
                ['Status', appointment.status?.replace(/_/g, ' ') ?? null],
                [
                  'Entity',
                  appointment.schedulableEntityId ? (
                    <span className={ID_CHIP}>{appointment.schedulableEntityId}</span>
                  ) : null,
                ],
                [
                  'Visit reason',
                  appointment.visitReasonId ? (
                    <span className={ID_CHIP}>{appointment.visitReasonId}</span>
                  ) : null,
                ],
                ['Last modified', appointment.lastModifiedAt ?? null],
              ]}
            />

            {appointment.participantsError && (
              <Alert color="yellow" radius="md" title="Participants could not be read">
                <span className="text-[13px]">
                  {appointment.participantsError}. This says nothing about whether the appointment
                  has a patient — only that this read failed.
                </span>
              </Alert>
            )}

            {!appointment.participantsError && !participants && (
              <Alert color="blue" radius="md">
                No participants were requested for this load.
              </Alert>
            )}

            {participants && !patient && (
              <Alert color="yellow" radius="md" title="No patient block">
                Zocdoc returned participants for this appointment but no patient inside them — which
                participant fields our partner account receives is unconfirmed, so this is worth
                confirming rather than treating as an empty booking.
              </Alert>
            )}

            {patient && (
              <>
                <Facts
                  title="Patient"
                  rows={[
                    [
                      'Name',
                      [patient.firstName, patient.lastName].filter(Boolean).join(' ') || null,
                    ],
                    ['Date of birth', formatDob(patient.dateOfBirth)],
                    ['Sex at birth', patient.sexAtBirth ?? null],
                    ['Gender identity', patient.genderIdentity?.join(', ') ?? null],
                    ['Phone', patient.phone ?? null],
                    ['Email', patient.email ?? null],
                    ['Address', address || null],
                    [
                      'Zocdoc patient id',
                      patient.patientId ? (
                        <span className={ID_CHIP}>{patient.patientId}</span>
                      ) : null,
                    ],
                    [
                      'beHuman patient id',
                      patient.developerPatientId ? (
                        // Present only when the booking came from us — this is the id we send as
                        // `developer_patient_id`, so it is how a Zocdoc row points back at a chart.
                        <span className={ID_CHIP}>{patient.developerPatientId}</span>
                      ) : null,
                    ],
                    [
                      'Booked by someone else',
                      patient.isBookedByAnotherPatient ? 'yes — a proxy booking' : null,
                    ],
                  ]}
                />

                <Facts
                  title="Insurance"
                  rows={[
                    ['Self pay', patient.insurance?.isSelfPay ? 'yes' : null],
                    [
                      'Plan id',
                      patient.insurance?.planId ? (
                        <span className={ID_CHIP}>{patient.insurance.planId}</span>
                      ) : null,
                    ],
                    ['Member id', patient.insurance?.memberId ?? null],
                    ['Group number', patient.insurance?.groupNumber ?? null],
                  ]}
                />
              </>
            )}

            {/* Yellow when the chart exists but is not bound to this booking: the creation and the
                invitation both succeeded, so this is not a failure — but the operator has one thing
                left to do, and a green banner would tell them they were finished. */}
            {imported && (
              <Alert
                color={imported.linkFailure ? 'yellow' : 'green'}
                radius="md"
                title={
                  imported.linkFailure
                    ? 'Patient created and invited, but not linked'
                    : 'Patient created, invited and linked'
                }
              >
                <span className="text-[13px]">
                  Chart <span className={ID_CHIP}>{imported.id}</span> now exists in beHuman and the
                  invitation has been sent.{' '}
                  {imported.linkFailure
                    ? `It does NOT carry this booking's Zocdoc patient id — ${imported.linkFailure}. Link it below once that is resolved.`
                    : 'It already carries this booking’s Zocdoc patient id, so nothing needs syncing.'}
                </span>
              </Alert>
            )}

            {/* Candidates, not a decision — the wording matters as much as the list. */}
            <Section title="beHuman charts that may be this patient">
              {linkError && (
                <Alert color="red" radius="md" mb="sm" title="Chart not linked">
                  <span className="text-[13px]">{linkError}</span>
                </Alert>
              )}
              {appointment.associatedPatientsError ? (
                <Alert color="yellow" radius="md" title="Chart lookup failed">
                  <span className="text-[13px]">
                    {appointment.associatedPatientsError}. Whether we already hold this patient is
                    unknown — not answered "no".
                  </span>
                </Alert>
              ) : appointment.associatedPatients.length === 0 ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className={cn([HINT, 'm-0'])}>
                    {patient?.email
                      ? 'No chart matches this email and date of birth.'
                      : 'Nothing to search on — Zocdoc gave no email for this patient.'}
                  </p>
                  {/* Only offered when there is something to import: a name and a date of birth are
                    the create form's own minimum, so without them the button would open a form
                    the operator has to fill from scratch anyway. */}
                  {canCreateChartFrom(patient) && (
                    <Button {...SECONDARY_BTN} onClick={() => setImporting(true)}>
                      Import patient
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <p className={cn([HINT, 'mt-0 mb-1.5'])}>
                    Matched on email and date of birth. Candidates for an operator to judge —
                    nothing here links a chart to this appointment.
                  </p>
                  <Table verticalSpacing={6} fz="xs">
                    <Table.Thead>
                      <Table.Tr className={TABLE_ROW}>
                        <Table.Th className={TABLE_HEAD}>Name</Table.Th>
                        <Table.Th className={TABLE_HEAD}>DOB</Table.Th>
                        <Table.Th className={TABLE_HEAD}>Email</Table.Th>
                        <Table.Th className={TABLE_HEAD}>Phone</Table.Th>
                        <Table.Th className={cn([TABLE_HEAD, 'w-[190px]'])}>Actions</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {appointment.associatedPatients.map((candidate) => (
                        <Table.Tr key={candidate.patientId} className={TABLE_ROW}>
                          <Table.Td className="font-bold text-[#1F2733]">
                            {candidate.firstName ?? '—'}
                          </Table.Td>
                          <Table.Td>{candidate.dob ?? '—'}</Table.Td>
                          <Table.Td className="break-all">{candidate.email ?? '—'}</Table.Td>
                          <Table.Td>{candidate.phoneNumber ?? '—'}</Table.Td>
                          <Table.Td>
                            <Group gap={4} wrap="nowrap">
                              {/* A real anchor, not an onClick: middle-click, cmd-click and
                                  "copy link" all work, and a new tab keeps this modal's
                                  unfinished booking review intact. */}
                              <Tooltip
                                label={`Open chart ${candidate.patientId} in a new tab`}
                                withArrow
                              >
                                <ActionIcon
                                  component="a"
                                  href={`/patients/${candidate.patientId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="subtle"
                                  color="gray"
                                  aria-label={`Open the chart for ${candidate.firstName ?? candidate.patientId} in a new tab`}
                                >
                                  <IconExternalLink size={16} />
                                </ActionIcon>
                              </Tooltip>
                              {/* Three states, and the third is the one worth getting right: this
                                  chart carries THIS Zocdoc id (done), it carries none (offer the
                                  link), or it carries a DIFFERENT one. That last case is not a
                                  dead end and not an append — a chart holds one Zocdoc patient, so
                                  it offers to *replace*, with the id being replaced on screen. */}
                              {zocdocPatientId &&
                              candidate.zocdocPatientIds.includes(zocdocPatientId) ? (
                                <span className="behuman-v2-mulish inline-flex items-center gap-1 rounded-md bg-[#DCFCE7] px-2 py-0.5 text-[11px] font-bold tracking-[0.04em] text-[#15803D] uppercase">
                                  <IconCheck size={12} />
                                  synced with beHuman
                                </span>
                              ) : candidate.zocdocPatientIds.length > 0 ? (
                                <>
                                  <span
                                    className="behuman-v2-mulish inline-flex items-center rounded-md bg-[#FEF3C7] px-2 py-0.5 text-[11px] font-bold tracking-[0.04em] text-[#B45309] uppercase"
                                    title={`This chart carries Zocdoc patient ${candidate.zocdocPatientIds.join(', ')}`}
                                  >
                                    linked to {candidate.zocdocPatientIds.join(', ')}
                                  </span>
                                  <Button
                                    size="compact-xs"
                                    variant="light"
                                    color="#B45309"
                                    leftSection={<IconRefresh size={14} />}
                                    className="behuman-v2-mulish !font-bold"
                                    loading={linking === candidate.patientId}
                                    disabled={!zocdocPatientId || linking !== null}
                                    title={
                                      zocdocPatientId
                                        ? `Drop ${candidate.zocdocPatientIds.join(', ')} from this chart and record ${zocdocPatientId} instead`
                                        : 'Zocdoc gave no patient id for this appointment, so there is nothing to link'
                                    }
                                    onClick={() =>
                                      void confirmSamePatient(candidate.patientId, true)
                                    }
                                  >
                                    Replace the link
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  size="compact-xs"
                                  variant="light"
                                  color="#E5533D"
                                  leftSection={<IconUserCheck size={14} />}
                                  className="behuman-v2-mulish !font-bold"
                                  loading={linking === candidate.patientId}
                                  disabled={!zocdocPatientId || linking !== null}
                                  title={
                                    zocdocPatientId
                                      ? `Write Zocdoc patient ${zocdocPatientId} onto this chart`
                                      : 'Zocdoc gave no patient id for this appointment, so there is nothing to link'
                                  }
                                  onClick={() => void confirmSamePatient(candidate.patientId)}
                                >
                                  Same patient
                                </Button>
                              )}
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </>
              )}
            </Section>

            <Facts
              title="Participants call"
              rows={[
                [
                  'Provider',
                  participants?.providerId ? (
                    <span className="flex flex-wrap items-center gap-1.5">
                      {/* The name first, the id second: the id is what the API needs and the name
                          is what the person reading this needs. Same resolution as the table's
                          practitioner column — the beHuman doctor when the provider is mapped,
                          Zocdoc's own name when it is not. */}
                      {(appointment.provider?.practitionerName ??
                      appointment.provider?.zocdocName) ? (
                        <span className="text-[13px] font-bold text-[#1F2733]">
                          {appointment.provider?.practitionerName ??
                            appointment.provider?.zocdocName}
                        </span>
                      ) : null}
                      {appointment.provider?.practitionerId ? (
                        <a
                          href={`/schedule?practitioner=${appointment.provider.practitionerId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="behuman-v2-mulish text-[12px] font-bold text-[#E5533D] no-underline hover:underline"
                        >
                          Calendar
                        </a>
                      ) : (
                        <span className="behuman-v2-mulish inline-flex items-center rounded-md bg-[#FEF3C7] px-1.5 py-0.5 text-[11px] font-bold tracking-[0.04em] text-[#B45309] uppercase">
                          unmapped
                        </span>
                      )}
                      <span className={ID_CHIP}>{participants.providerId}</span>
                    </span>
                  ) : null,
                ],
                [
                  'Location',
                  participants?.locationId ? (
                    <span className={ID_CHIP}>{participants.locationId}</span>
                  ) : null,
                ],
                ['Documents', participants?.documentNames?.join(', ') ?? null],
                [
                  'Zocdoc request id',
                  participants?.requestId ? (
                    <span className={ID_CHIP}>{participants.requestId}</span>
                  ) : null,
                ],
              ]}
            />

            <Section title="Raw">
              <p className={cn([HINT, 'mt-0 mb-1.5'])}>
                Both records exactly as Zocdoc sent them. The payload shape is still unconfirmed, so
                this is the authority when a mapped field looks wrong.
              </p>
              <ScrollArea.Autosize mah={300} type="auto">
                <pre className="m-0 rounded-lg bg-[#F8F9FB] p-3 font-mono text-[11.5px] leading-relaxed text-[#4A5160]">
                  {JSON.stringify(
                    { appointment: appointment.raw, participants: participants?.raw ?? null },
                    null,
                    2,
                  )}
                </pre>
              </ScrollArea.Autosize>
            </Section>
          </div>
        )}
      </Modal>

      {/* A sibling rather than a child modal: nesting two Mantine modals fights over focus, and
          the create form only ever needs to sit above this one. It is the same shared component
          the patient list uses, so the invitation flow is the app's, not a copy of it. */}
      {patient && (
        <CreateUpdatePatientModal
          // Keyed by appointment: Mantine's `useForm` reads `initialValues` on mount only, so a
          // long-lived instance would keep the first appointment's prefill and quietly offer the
          // previous patient's details for the next one. The key forces a fresh form per row.
          key={appointment?.id}
          isOpen={importing}
          onClose={() => setImporting(false)}
          prefill={prefillFromZocdoc(patient)}
          // The chart is bound to this booking the moment it exists, so the operator is not sent
          // back to the candidate list to press "Same patient" on the chart they just made. A
          // failure here is reported beside the creation rather than as one: the chart exists and
          // the invitation has gone out, and an operator told only "could not link" would
          // reasonably conclude neither happened and make a second chart.
          successCallback={(created) => {
            setImporting(false)
            void linkCreatedChart(created).then((result) => {
              setImported({
                id: created.id ?? 'created',
                ...(result.detail && !result.linked && { linkFailure: result.detail }),
              })
              onImported?.()
            })
          }}
        />
      )}
    </>
  )
}
